import { useEffect, useState } from "react";

import type { AgentSdk } from "../agentSdk";

/** @returns the wrapped ExpertUniversitySdk if in training mode, otherwise the original sdk */
export function useExpertUniversitySdk(isTrainingMode: boolean, sdk: AgentSdk | undefined): AgentSdk | undefined {
    const [sdkState, setSdkState] = useState(sdk);

    useEffect(() => {
        if (!isTrainingMode || !sdk) {
            setSdkState(sdk);
            return;
        }

        /**
         * Flag to track whether the effect has been cleaned up.
         *
         * If the component unmounts or the dependencies change before the async operation finishes then we don't want
         * to call `setSdkState()` as it is no longer applicable.
         *
         * @see https://react.dev/reference/react/useEffect#fetching-data-with-effects
         */
        let ignore = false;

        const wrapSdk = async () => {
            try {
                const { default: ExpertUniversitySdk } = await import("@/expert-university/ExpertUniversitySdk");
                if (ignore) return;
                setSdkState(new ExpertUniversitySdk(sdk) as unknown as AgentSdk);
            } catch (error) {
                if (!ignore) {
                    console.error("Failed to load ExpertUniversitySdk:", error);
                }
            }
        };

        wrapSdk();

        return () => {
            ignore = true;
        };
    }, [isTrainingMode, sdk]);

    return sdkState;
}
